import {forwardRef} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {useGetInsightsMetrics} from '@features/insights/queries/useGetInsightsMetrics'
import {AlertTriangleIcon, CheckCircleIcon, Icon, ImageUpIcon, MessageTextSquare01Icon} from '@components/ui/icon'
import {MetricsCard} from './metrics-card'
import {Skeleton} from '@components/ui/skeleton'
// import {MetricsBarChart} from './metrics-bar-chart'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams'
import {InsightsFilterParam} from './insights-filters-select'
import {ErrorBox} from '@components/commons/error-box'
import {Button} from '@components/ui/button'
import {Separator} from '@components/ui/separator'

type MetricCardType = {
    title: string
    description: string
    value: string
    icon: Icon
}

export const Metrics = forwardRef<HTMLDivElement, {isInsightsVisible: boolean}>(({isInsightsVisible}, ref) => {
    const {t} = useTranslation()
    const {searchParams} = useParsedSearchParams(InsightsFilterParam)
    const {
        data: insightsMetrics,
        isLoading,
        isError,
        refetch
    } = useGetInsightsMetrics(searchParams.period, isInsightsVisible)

    const metricsCardData = [
        {
            title: t('insights:metrics:sets_meeting_coverage_title'),
            description: t('insights:metrics:sets_meeting_coverage_description'),
            value:
                insightsMetrics?.setsMeetingMinimumCoveragePercentage != null
                    ? `${Math.round(insightsMetrics.setsMeetingMinimumCoveragePercentage)}%`
                    : '-',
            icon: ImageUpIcon
        },
        {
            title: t('insights:metrics:response_rate_title'),
            description: t('insights:metrics:response_rate_description'),
            value:
                insightsMetrics?.assignmentResponseRate != null
                    ? `${Math.round(insightsMetrics.assignmentResponseRate)}%`
                    : '-',
            icon: MessageTextSquare01Icon
        },
        {
            title: t('insights:metrics:acceptance_rate_title'),
            description: t('insights:metrics:acceptance_rate_description'),
            value:
                insightsMetrics?.assignmentAcceptanceRate != null
                    ? `${Math.round(insightsMetrics.assignmentAcceptanceRate)}%`
                    : '-',
            icon: CheckCircleIcon
        }
    ] as const satisfies MetricCardType[]

    return (
        <div ref={ref} className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
                <h3 className="text-display-xs font-medium text-neutral-900">{t('insights:metrics:header_title')}</h3>
                <p className="text-lg text-neutral-700">{t('insights:metrics:header_subtitle')}</p>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium whitespace-nowrap text-neutral-900">
                    {t('insights:metrics:outcomes_section')}
                </span>
                <Separator orientation="horizontal" className="flex-1 bg-neutral-300" />
            </div>
            {isError ? (
                <ErrorBox
                    title={t('errors:default')}
                    icon={<AlertTriangleIcon />}
                    cta={<Button onClick={() => refetch()}>{t('commons:retry')}</Button>}
                />
            ) : (
                <>
                    <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-3 md:gap-6">
                        {metricsCardData.map(item =>
                            isLoading ? (
                                <Skeleton height="100%" width="100%" key={item?.title} />
                            ) : (
                                <MetricsCard {...item} key={item?.title} />
                            )
                        )}
                    </div>

                    {/* TODO: restore the image quality chart when it is re-enabled for photographers @ImageQualityMetric */}
                    {/* {isLoading ? (
                        <Skeleton height="240px" width="100%" />
                    ) : (
                        <MetricsBarChart chartData={insightsMetrics?.imageQualityChart} />
                    )} */}
                </>
            )}
        </div>
    )
})
